import './style.css'
import ReactDOM from 'react-dom/client'
import { Canvas } from '@react-three/fiber'
import { KeyboardControls } from '@react-three/drei'
import { Suspense, useState, useEffect } from 'react'
import Experience from './Experience.jsx'
import Interface from './Interface.jsx'

// Beautiful Game Loader with progress animation
function GameLoader({ onLoadComplete }) {
    const [progress, setProgress] = useState(0)
    const [isComplete, setIsComplete] = useState(false)

    useEffect(() => {
        // Fast progress to 99%
        const progressInterval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 99) {
                    clearInterval(progressInterval)
                    return 99
                }
                // Exponential slow down as we approach 99
                const remaining = 99 - prev
                const increment = Math.max(1, remaining * 0.15)
                return Math.min(99, prev + increment)
            })
        }, 50)

        return () => clearInterval(progressInterval)
    }, [])

    useEffect(() => {
        if (progress >= 99 && !isComplete) {
            // 1 second delay at 99%, then complete
            const completeTimeout = setTimeout(() => {
                setProgress(100)
                setIsComplete(true)
                // Small delay before hiding loader
                setTimeout(() => {
                    onLoadComplete()
                }, 400)
            }, 1000)

            return () => clearTimeout(completeTimeout)
        }
    }, [progress, isComplete, onLoadComplete])

    return (
        <div className={`game-loader ${isComplete ? 'fade-out' : ''}`}>
            <div className="loader-content">
                {/* Logo */}
                <div className="loader-logo">
                    <span className="loader-icon">❄️</span>
                    <h1 className="loader-title">ICE ROLL</h1>
                </div>

                {/* Progress bar */}
                <div className="loader-progress-container">
                    <div className="loader-progress-bar">
                        <div
                            className="loader-progress-fill"
                            style={{ width: `${progress}%` }}
                        >
                            <div className="loader-progress-glow"></div>
                        </div>
                    </div>
                    <div className="loader-progress-text">
                        <span className="loader-percentage">{Math.round(progress)}%</span>
                        <span className="loader-status">
                            {progress < 99 ? 'Loading assets...' : progress < 100 ? 'Almost ready...' : 'Let\'s go!'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Developer credit */}
            <div className="loader-credit">
                Made with ❄️ by <span>Unfor Dev</span>
            </div>
        </div>
    )
}

function App() {
    const [isLoading, setIsLoading] = useState(true)

    return (
        <KeyboardControls
            map={ [
                { name: 'forward', keys: ['ArrowUp', 'KeyW'] },
                { name: 'backward', keys: ['ArrowDown', 'KeyS'] },
                { name: 'leftward', keys: ['ArrowLeft', 'KeyA'] },
                { name: 'rightward', keys: ['ArrowRight', 'KeyD'] },
                { name: 'jump', keys: ['Space'] }
            ] }
        >
            {isLoading && <GameLoader onLoadComplete={() => setIsLoading(false)} />}

            <Canvas
                shadows
                camera={ {
                    fov: 45,
                    near: 0.1,
                    far: 200,
                    position: [2.5, 4, 6]
                } }
            >
                <Suspense fallback={ null }>
                    <Experience />
                </Suspense>
            </Canvas>

            <Interface />
        </KeyboardControls>
    )
}

const root = ReactDOM.createRoot(document.querySelector('#root'))
root.render(<App />)
